import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { ChevronLeft, ChevronRight, X, Heart } from 'lucide-react';

import { toast } from '../hooks/use-toast';
import { getProductById, getProducts, getProductsByCategory } from '../services/productService';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { formatMoney, getProductPrice, getProductCompareAtPrice } = useCurrency();

  const { wishlistIds, toggleWishlist } = useWishlist();
  const { userDoc, updateUser } = useAuth();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [isImageFading, setIsImageFading] = useState(false);
  const fadeTimerRef = useRef(null);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [wishlistActive, setWishlistActive] = useState(false);

  const [openSection, setOpenSection] = useState('');
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadProduct = async () => {
      setIsLoading(true);
      try {
        const loadedProduct = await getProductById(id);
        setProduct(loadedProduct);
        setSelectedImage(0);
        setSelectedSize('');
        setSelectedColor('');
        setOpenSection('');
        setWishlistActive((wishlistIds || []).includes(String(id)));

        if (loadedProduct) {
          // Load related products
          const categorySlug =
            (Array.isArray(loadedProduct.categories) && loadedProduct.categories[0]) || loadedProduct.category;
          const related = categorySlug ? await getProductsByCategory(categorySlug) : [];
          const filtered = (Array.isArray(related) ? related : []).filter((p) => String(p?.id) !== String(id));
          const prioritized = filtered
            .slice()
            .sort((a, b) => Number(Boolean(b?.inStock)) - Number(Boolean(a?.inStock)))
            .slice(0, 4);
          setRelatedProducts(prioritized);
        }
      } catch (error) {
        console.error('Error loading product:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [id]);

  useEffect(() => {
    if (!product?.id) return;
    const current = Array.isArray(userDoc?.recentlyViewed) ? userDoc.recentlyViewed.map(String) : [];
    const next = [String(product.id), ...current.filter((x) => String(x) !== String(product.id))].slice(0, 8);
    if (typeof updateUser === 'function') {
      updateUser({ recentlyViewed: next });
    }
  }, [product?.id]);

  useEffect(() => {
    const load = async () => {
      try {
        const ids = Array.isArray(userDoc?.recentlyViewed) ? userDoc.recentlyViewed.map(String) : [];
        const wanted = ids.filter((x) => String(x) !== String(product?.id)).slice(0, 4);
        if (wanted.length === 0) {
          setRecentlyViewed([]);
          return;
        }
        const all = await getProducts();
        const byId = new Map((Array.isArray(all) ? all : []).map((p) => [String(p?.id), p]));
        setRecentlyViewed(wanted.map((x) => byId.get(String(x))).filter(Boolean));
      } catch {
        setRecentlyViewed([]);
      }
    };

    load();
  }, [product?.id, userDoc?.recentlyViewed]);

  useEffect(() => {
    setWishlistActive((wishlistIds || []).includes(String(id)));
  }, [id, wishlistIds]);

  useEffect(() => {
    return () => {
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!lightboxOpen) return;
    const orderImages = (imgs, primaryIdx) => {
      const list = Array.isArray(imgs) ? imgs.filter(Boolean) : [];
      if (list.length <= 1) return list;
      const idx = Number(primaryIdx ?? 0);
      if (!Number.isFinite(idx) || idx <= 0 || idx >= list.length) return list;
      return [list[idx], ...list.slice(0, idx), ...list.slice(idx + 1)];
    };

    const getDisplayImagesLen = () => {
      const common = orderImages(product?.images, product?.primaryImageIndex);
      const colorKey = String(selectedColor || '').toUpperCase();
      const entry =
        colorKey && product?.colorImages && typeof product.colorImages === 'object'
          ? product.colorImages[colorKey]
          : null;
      const colorImgs = orderImages(entry?.images, entry?.primaryImageIndex);
      const merged = colorKey ? (colorImgs.length > 0 ? colorImgs : common) : common;
      return merged.length || 0;
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setLightboxOpen(false);
        return;
      }
      if (e.key === 'ArrowLeft') {
        setSelectedImage((prev) => {
          const len = getDisplayImagesLen();
          if (len <= 1) return prev;
          return (prev - 1 + len) % len;
        });
      }
      if (e.key === 'ArrowRight') {
        setSelectedImage((prev) => {
          const len = getDisplayImagesLen();
          if (len <= 1) return prev;
          return (prev + 1) % len;
        });
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    lightboxOpen,
    selectedColor,
    product?.images,
    product?.primaryImageIndex,
    product?.colorImages,
  ]);

  const buildOrderedImages = (imgs, primaryIdx) => {
    const list = Array.isArray(imgs) ? imgs.filter(Boolean) : [];
    if (list.length <= 1) return list;
    const idx = Number(primaryIdx ?? 0);
    if (!Number.isFinite(idx) || idx <= 0 || idx >= list.length) return list;
    return [list[idx], ...list.slice(0, idx), ...list.slice(idx + 1)];
  };

  const commonImages = buildOrderedImages(product?.images, product?.primaryImageIndex);
  const selectedColorKey = String(selectedColor || '').toUpperCase();
  const colorEntry =
    selectedColorKey && product?.colorImages && typeof product.colorImages === 'object'
      ? product.colorImages[selectedColorKey]
      : null;
  const colorSpecificImages = buildOrderedImages(colorEntry?.images, colorEntry?.primaryImageIndex);
  const displayImages = selectedColorKey
    ? colorSpecificImages.length > 0
      ? colorSpecificImages
      : commonImages
    : commonImages;

  const variantStock = useMemo(() => {
    const raw = product?.variantStock;
    if (!raw || typeof raw !== 'object') return null;
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
      const key = String(k);
      const qty = Number(v);
      out[key] = Number.isFinite(qty) ? qty : 0;
    }
    return out;
  }, [product?.variantStock]);

  const getVariantKey = (color, size) => `${String(color || '').toUpperCase()}|${String(size || '').toUpperCase()}`;
  const getVariantQty = (color, size) => {
    if (!variantStock) return null;
    if (!color || !size) return null;
    const key = getVariantKey(color, size);
    return Number(variantStock[key] || 0);
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center">
        <p className="text-gray-500">Loading product...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center">
        <p className="text-gray-500 mb-4">Product not found</p>
        <button
          onClick={() => navigate('/shop/new-arrivals')}
          className="text-sm underline hover:opacity-60"
        >
          Back to shop
        </button>
      </div>
    );
  }

  const stockQty = Number(product.stockQuantity || 0);
  const stockMessage = !product.inStock
    ? 'Out of stock'
    : Number.isFinite(stockQty) && stockQty > 0 && stockQty <= 2
      ? `Only ${stockQty} left`
      : 'In stock';

  const selectedVariantQty = selectedColor && selectedSize ? getVariantQty(selectedColor, selectedSize) : null;
  const selectedVariantMessage =
    variantStock && selectedColor && selectedSize
      ? selectedVariantQty > 0
        ? selectedVariantQty <= 2
          ? `Only ${selectedVariantQty} left in ${selectedColor} / ${selectedSize}`
          : `${selectedColor} / ${selectedSize} in stock`
        : `${selectedColor} / ${selectedSize} sold out`
      : '';

  const onToggleWishlist = async () => {
    try {
      const result = await toggleWishlist(product.id);
      const nextActive = (result?.ids || []).includes(String(product.id));
      setWishlistActive(nextActive);
      toast({
        title: nextActive ? 'Saved to wishlist' : 'Removed from wishlist',
        description: nextActive ? 'Added to your wishlist.' : 'Removed from your wishlist.',
      });
    } catch (e) {
      toast({
        title: 'Wishlist update failed',
        description: String(e?.message || 'Please try again.'),
      });
    }
  };

  const handleAddToCart = () => {
    if (getProductPrice(product) === null) {
      toast({
        title: 'Not available',
        description: 'This product is not available in your selected region/currency.',
      });
      return;
    }
    if (!selectedSize) {
      toast({
        title: "Please select a size",
        description: "You must choose a size before adding to cart.",
      });

      return;
    }
    if (variantStock && selectedColor && selectedSize && Number(getVariantQty(selectedColor, selectedSize) || 0) <= 0) {
      toast({
        title: 'Variant unavailable',
        description: 'Please select another size/color.',
      });
      return;
    }
    if (!selectedColor) {
      toast({
        title: "Please select a color",
        description: "You must choose a color before adding to cart.",
      });
      return;
    }
    if (!product.inStock) {
      toast({
        title: "Product unavailable",
        description: "This product is currently out of stock.",
      });
      return;
    }

    addToCart(product, selectedSize, selectedColor, 1);
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart.`,
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm mb-8 hover:opacity-60 transition-opacity"
      >
        <ChevronLeft size={16} />
        Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Images */}
        <div>
          <div className="relative aspect-square bg-gray-100 mb-4 w-full">
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="w-full h-full block cursor-zoom-in"
              aria-label="Open image gallery"
            >
              <img
                src={displayImages[selectedImage] || displayImages[0]}
                alt={product.name}
                className={`w-full h-full object-cover transition-opacity duration-150 ${
                  isImageFading ? 'opacity-0' : 'opacity-100'
                }`}
              />
            </button>

            {displayImages.length > 1 && selectedImage > 0 ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedImage((prev) => Math.max(0, prev - 1));
                }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 border border-gray-200 flex items-center justify-center hover:bg-white hover:border-gray-300 transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft size={18} />
              </button>
            ) : null}

            {displayImages.length > 1 && selectedImage < displayImages.length - 1 ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedImage((prev) => Math.min(displayImages.length - 1, prev + 1));
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 border border-gray-200 flex items-center justify-center hover:bg-white hover:border-gray-300 transition-colors"
                aria-label="Next image"
              >
                <ChevronRight size={18} />
              </button>
            ) : null}
          </div>
          {displayImages.length > 1 && (
            <div className="grid grid-cols-4 gap-4">
              {displayImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`aspect-square bg-gray-100 border-2 transition-colors ${
                    selectedImage === index ? 'border-black' : 'border-transparent'
                  }`}
                >
                  <img
                    src={image}
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-medium tracking-wide mb-2">{product.name}</h1>
              {(() => {
                const price = getProductPrice(product);
                const mrp = getProductCompareAtPrice(product);
                if (price !== null && mrp !== null && Number(mrp) > Number(price)) {
                  return (
                    <div className="text-xl">
                      <span className="text-gray-500 line-through mr-3">{formatMoney(mrp)}</span>
                      <span>{formatMoney(price)}</span>
                    </div>
                  );
                }
                return <p className="text-xl">{formatMoney(price)}</p>;
              })()}
              <p className={`text-sm mt-2 ${product.inStock ? 'text-gray-600' : 'text-gray-500'}`}>{stockMessage}</p>
            </div>
            <button
              type="button"
              onClick={onToggleWishlist}
              className={`border w-10 h-10 flex items-center justify-center transition-colors ${
                wishlistActive ? 'border-black bg-black text-white' : 'border-gray-300 hover:border-black'
              }`}
              aria-label={wishlistActive ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <Heart size={18} />
            </button>
          </div>

          <div className="h-6" />

          {/* Description */}
          <p className="text-sm text-gray-600 mb-8">{product.description}</p>

          {/* Color Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-3">COLOR: {selectedColor}</label>
            <div className="flex gap-2">
              {product.colors.map((color) => (
                <button
                  key={color}
                  onClick={() => {
                    const c = String(color);
                    if (variantStock && selectedSize) {
                      const qty = getVariantQty(c, selectedSize);
                      if (qty !== null && Number(qty) <= 0) return;
                    }
                    if (fadeTimerRef.current) {
                      clearTimeout(fadeTimerRef.current);
                      fadeTimerRef.current = null;
                    }
                    setIsImageFading(true);
                    setSelectedColor(c);
                    setSelectedImage(0);
                    fadeTimerRef.current = setTimeout(() => {
                      setIsImageFading(false);
                      fadeTimerRef.current = null;
                    }, 160);
                  }}
                  className={`px-4 py-2 text-sm border-2 transition-colors ${
                    variantStock && selectedSize && Number(getVariantQty(String(color), selectedSize) || 0) <= 0
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : selectedColor === color
                        ? 'border-black bg-black text-white'
                        : 'border-gray-300 hover:border-black'
                  }`}
                >
                  {color}
                </button>
              ))}
            </div>
          </div>

          {/* Size Selection */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium">SIZE: {selectedSize}</label>
              <button
                type="button"
                onClick={() => setSizeGuideOpen(true)}
                className="text-sm underline hover:opacity-60"
              >
                Size guide
              </button>
            </div>
            {selectedVariantMessage ? <div className="text-sm text-gray-600 mb-3">{selectedVariantMessage}</div> : null}
            <div className="grid grid-cols-5 gap-2">
              {product.sizes.map(size => (
                <button
                  key={size}
                  onClick={() => {
                    const s = String(size);
                    if (variantStock && selectedColor) {
                      const qty = getVariantQty(selectedColor, s);
                      if (qty !== null && Number(qty) <= 0) return;
                    }
                    setSelectedSize(s);
                  }}
                  className={`py-2 text-sm border-2 transition-colors ${
                    variantStock && selectedColor && Number(getVariantQty(selectedColor, String(size)) || 0) <= 0
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : selectedSize === String(size)
                        ? 'border-black bg-black text-white'
                        : 'border-gray-300 hover:border-black'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Add to Cart Button */}
          <button
            onClick={handleAddToCart}
            disabled={!product.inStock || (variantStock && selectedColor && selectedSize && Number(getVariantQty(selectedColor, selectedSize) || 0) <= 0)}
            className={`w-full py-4 text-sm font-medium tracking-wide transition-colors ${
              product.inStock
                ? 'bg-black text-white hover:bg-gray-800'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {product.inStock ? 'ADD TO BAG' : 'OUT OF STOCK'}
          </button>

          <div className="mt-8 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setOpenSection((p) => (p === 'fit' ? '' : 'fit'))}
              className="w-full flex items-center justify-between py-5 text-sm font-medium"
            >
              FIT NOTES
              <span className="text-gray-500">{openSection === 'fit' ? '−' : '+'}</span>
            </button>
            {openSection === 'fit' ? (
              <div className="pb-5 text-sm text-gray-600">
                {product.fitNotes ? product.fitNotes : 'True to size. For a more relaxed fit, size up.'}
              </div>
            ) : null}

            <div className="border-t border-gray-200" />
            <button
              type="button"
              onClick={() => setOpenSection((p) => (p === 'care' ? '' : 'care'))}
              className="w-full flex items-center justify-between py-5 text-sm font-medium"
            >
              FABRIC & CARE
              <span className="text-gray-500">{openSection === 'care' ? '−' : '+'}</span>
            </button>
            {openSection === 'care' ? (
              <div className="pb-5 text-sm text-gray-600">
                {product.fabricCare ? (
                  product.fabricCare
                ) : (
                  <div className="space-y-2">
                    <div>Premium materials with a soft hand-feel.</div>
                    <div>Machine wash cold. Hang dry or tumble low.</div>
                  </div>
                )}
              </div>
            ) : null}

            <div className="border-t border-gray-200" />
            <button
              type="button"
              onClick={() => setOpenSection((p) => (p === 'shipping' ? '' : 'shipping'))}
              className="w-full flex items-center justify-between py-5 text-sm font-medium"
            >
              SHIPPING & RETURNS
              <span className="text-gray-500">{openSection === 'shipping' ? '−' : '+'}</span>
            </button>
            {openSection === 'shipping' ? (
              <div className="pb-5 text-sm text-gray-600">
                <div className="space-y-2">
                  <div>Fast dispatch and premium packaging.</div>
                  <div>
                    See{' '}
                    <Link to="/shipping" className="underline hover:opacity-60">
                      shipping details
                    </Link>
                    {' '}for rates and timelines.
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Related Products */}
      <div className="mt-20">
        <h2 className="text-xl font-medium tracking-wide mb-8">YOU MAY ALSO LIKE</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {relatedProducts.map(relatedProduct => (
            <button
              key={relatedProduct.id}
              onClick={() => {
                navigate(`/product/${relatedProduct.id}`);
                window.scrollTo(0, 0);
              }}
              className="group text-left"
            >
              <div className="aspect-square bg-gray-100 mb-3 overflow-hidden">
                <img
                  src={(() => {
                    const imgs = Array.isArray(relatedProduct.images) ? relatedProduct.images : [];
                    const idx = Number(relatedProduct.primaryImageIndex ?? 0);
                    return imgs[(Number.isFinite(idx) && idx >= 0 ? idx : 0)] || imgs[0];
                  })()}
                  alt={relatedProduct.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3 className="text-sm font-medium mb-1">{relatedProduct.name}</h3>
              {(() => {
                const price = getProductPrice(relatedProduct);
                const mrp = getProductCompareAtPrice(relatedProduct);
                if (price !== null && mrp !== null && Number(mrp) > Number(price)) {
                  return (
                    <div className="text-sm">
                      <span className="text-gray-500 line-through mr-2">{formatMoney(mrp)}</span>
                      <span>{formatMoney(price)}</span>
                    </div>
                  );
                }
                return <p className="text-sm">{formatMoney(price)}</p>;
              })()}
            </button>
          ))}
        </div>
      </div>

      {sizeGuideOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-6">
          <div className="bg-white w-full max-w-lg p-6 relative">
            <button
              type="button"
              onClick={() => setSizeGuideOpen(false)}
              className="absolute top-4 right-4"
              aria-label="Close size guide"
            >
              <X size={20} />
            </button>
            <h3 className="text-lg font-medium tracking-wide mb-4">SIZE GUIDE</h3>
            <p className="text-sm text-gray-600 mb-4">
              Available sizes: {(product.sizes || []).join(', ')}
            </p>
            <button
              type="button"
              onClick={() => {
                setSizeGuideOpen(false);
                navigate('/size-guide');
              }}
              className="w-full border border-black py-3 text-sm font-medium tracking-wide hover:bg-black hover:text-white transition-colors"
            >
              OPEN FULL SIZE GUIDE
            </button>
          </div>
        </div>
      )}

      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-6 right-6 text-white"
            aria-label="Close gallery"
          >
            <X size={24} />
          </button>

          {displayImages.length > 1 && (
            <button
              type="button"
              onClick={() =>
                setSelectedImage((prev) => {
                  const len = displayImages.length;
                  return (prev - 1 + len) % len;
                })
              }
              className="absolute left-4 md:left-10 text-white p-3 hover:opacity-80"
              aria-label="Previous image"
            >
              <ChevronLeft size={28} />
            </button>
          )}

          <div className="w-full max-w-5xl px-6">
            <div className="w-full">
              <img
                src={displayImages[selectedImage] || displayImages[0]}
                alt={product.name}
                className="w-full max-h-[80vh] object-contain"
              />
            </div>

            {displayImages.length > 1 && (
              <div className="mt-6 flex gap-3 overflow-x-auto">
                {displayImages.map((image, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 flex-shrink-0 border-2 ${
                      selectedImage === index ? 'border-white' : 'border-transparent'
                    }`}
                    aria-label={`Select image ${index + 1}`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {displayImages.length > 1 && (
            <button
              type="button"
              onClick={() =>
                setSelectedImage((prev) => {
                  const len = displayImages.length;
                  return (prev + 1) % len;
                })
              }
              className="absolute right-4 md:right-10 text-white p-3 hover:opacity-80"
              aria-label="Next image"
            >
              <ChevronRight size={28} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductDetail;